// Aug-2026 — Phase 2A: Project PR create/edit form (Draft & Rejected only).
// All totals shown here are estimates; the SERVER recalculates authoritatively.
// Jun-2026 — Item picker now reads the full Item Master via /items/_pickable
// (same pattern as Estimates/PO). PR-level (max 3) and line-level attachments
// can be chosen right here; images of ANY size are accepted and auto-compressed
// below 500 KB client-side (DB optimisation) before upload.
import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Paperclip } from "lucide-react";
import { SearchableSelect } from "@/components/SearchableSelect";
import { compressImage } from "@/lib/imageCompress";

const TAX_OPTS = ["", "0", "3", "5", "6", "9", "12", "18", "28"];
const PRIORITIES = ["", "Low", "Normal", "High", "Urgent"];
const LINE_DOC_MAX = 500 * 1024;        // stored cap (server-enforced)
const PDF_MAX = 5 * 1024 * 1024;
const IMG_MAX = 500 * 1024;             // stored cap (server-enforced)

const emptyLine = () => ({
  item_id: "", item_code: "", item_name: "", item_type_snapshot: "",
  uom: "", quantity: "", estimated_rate: "", tax_percent: "",
  required_date: "", boq_element_id: "", work_activity: "", line_remarks: "",
  description: "", brand: "", _doc: null,
});

function validPrFile(f) {
  const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name);
  const isImg = /^image\/(jpeg|png)$/.test(f.type) || /\.(jpe?g|png)$/i.test(f.name);
  if (!isPdf && !isImg) return "Only PDF, JPG or PNG files are allowed";
  if (isPdf && f.size > PDF_MAX) return `${f.name}: PDF must be 5 MB or smaller`;
  return ""; // images: no pick cap — auto-compressed below 500 KB
}

const isImageFile = (f) => /^image\//.test(f.type || "") || /\.(jpe?g|png)$/i.test(f.name || "");

export default function ProjectPRForm({ project, existing, commercialsVisible, onSaved, onClose }) {
  const pid = project.id;
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  const [uoms, setUoms] = useState([]);
  const [boqEls, setBoqEls] = useState([]);
  const [busy, setBusy] = useState(false);
  const [totEdit, setTotEdit] = useState({});       // transient per-line Total-inc-GST typing buffer
  const [prFiles, setPrFiles] = useState([]);
  const existingAtt = existing?.attachments?.length || 0;
  const [head, setHead] = useState({
    pr_date: existing?.pr_date || new Date().toISOString().slice(0, 10),
    vendor_id: existing?.vendor_id || "",
    required_by_date: existing?.required_by_date || "",
    delivery_location: existing ? (existing.delivery_location || "") : (project.project_address || ""),
    purpose: existing?.purpose || "",
    priority: existing?.priority || "",
    remarks: existing?.remarks || "",
  });
  const [lines, setLines] = useState(
    existing?.line_items?.length
      ? existing.line_items.map(l => ({ ...emptyLine(), ...l,
          quantity: l.quantity ?? "", estimated_rate: l.estimated_rate ?? "",
          tax_percent: l.tax_percent ?? "" }))
      : [emptyLine()]);

  useEffect(() => {
    api.get("/vendors/picker")
      .then(r => setVendors(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/items/_pickable")
      .then(r => setItems(Array.isArray(r.data) ? r.data : (r.data?.rows || []))).catch(() => {});
    api.get("/item-groups")
      .then(r => setGroups(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/uom-master", { params: { limit: 500 } })
      .then(r => setUoms((Array.isArray(r.data) ? r.data : []).map(u => u.name))).catch(() => {});
    api.get(`/projects/${pid}/boq/baseline-elements`)
      .then(r => setBoqEls(r.data.elements || [])).catch(() => setBoqEls([]));
  }, [pid]);

  const setLine = (i, patch) => setLines(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));

  // Same rule as Estimates: match by group_id, or legacy free-text group_name.
  const itemInGroup = (it, g) => {
    if (!g) return false;
    if ((it.group_id || "") && it.group_id === g.id) return true;
    const inm = (it.group_name || "").trim().toLowerCase();
    const gnm = (g.name || "").trim().toLowerCase();
    return !!inm && inm === gnm;
  };
  const filteredItems = useMemo(() => (groupFilter
    ? items.filter(it => itemInGroup(it, groups.find(g => g.id === groupFilter)))
    : items), [items, groups, groupFilter]);
  // Keep an already-selected item visible in its line's picker even when the
  // group filter would hide it.
  const pickerItemsFor = (l) => {
    if (!l.item_id || filteredItems.some(x => x.id === l.item_id)) return filteredItems;
    const sel = items.find(x => x.id === l.item_id);
    return sel ? [sel, ...filteredItems] : filteredItems;
  };

  const pickPrFiles = async (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = "";
    for (const f of chosen) {
      const err = validPrFile(f);
      if (err) return toast.error(err);
    }
    // Auto-compress images below the 500 KB stored cap (DB optimisation).
    const prepared = [];
    for (const f of chosen) {
      let out = f;
      if (isImageFile(f)) {
        try { out = await compressImage(f, IMG_MAX); } catch { out = f; }
        if (out.size > IMG_MAX)
          return toast.error(`${f.name}: could not compress below 500 KB — please pick a smaller image`);
      }
      prepared.push(out);
    }
    setPrFiles(fs => {
      const next = [...fs, ...prepared];
      if (existingAtt + next.length > 3) {
        toast.error(`Maximum 3 PR attachments${existingAtt ? ` (${existingAtt} already uploaded)` : ""}`);
        return fs;
      }
      return next;
    });
  };

  const pickLineDoc = async (i, e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(pdf|jpe?g|png)$/i.test(f.name) && !/^(application\/pdf|image\/(jpeg|png))$/.test(f.type))
      return toast.error("Only PDF, JPG or PNG allowed");
    let doc = f;
    if (isImageFile(f)) {
      try { doc = await compressImage(f, LINE_DOC_MAX); } catch { doc = f; }
      if (doc.size > LINE_DOC_MAX)
        return toast.error(`${f.name}: could not compress below 500 KB — please pick a smaller image`);
    } else if (f.size > LINE_DOC_MAX) {
      return toast.error(`${f.name}: line PDF must be 500 KB or smaller (got ${Math.ceil(f.size / 1024)} KB)`);
    }
    setLine(i, { _doc: doc });
  };

  const totals = useMemo(() => {
    let sub = 0, tax = 0;
    lines.forEach(l => {
      const q = parseFloat(l.quantity), r = l.estimated_rate === "" ? null : parseFloat(l.estimated_rate);
      if (!isNaN(q) && r !== null && !isNaN(r)) {
        const t = q * r; sub += t;
        if (l.tax_percent !== "") tax += t * parseFloat(l.tax_percent) / 100;
      }
    });
    return { sub, tax, total: sub + tax };
  }, [lines]);

  const save = async () => {
    const payload = {
      ...head,
      line_items: lines.map(l => ({
        line_uid: l.line_uid || undefined,
        item_id: l.item_id, uom: l.uom, quantity: l.quantity,
        estimated_rate: l.estimated_rate === "" ? null : l.estimated_rate,
        tax_percent: l.tax_percent === "" ? null : l.tax_percent,
        required_date: l.required_date, boq_element_id: l.boq_element_id,
        work_activity: l.work_activity, line_remarks: l.line_remarks,
        description: l.description, brand: l.brand,
      })),
    };
    setBusy(true);
    try {
      const r = existing
        ? await api.put(`/projects/${pid}/prs/${existing.id}`, payload)
        : await api.post(`/projects/${pid}/prs`, payload);
      const saved = r.data;
      const failures = [];
      for (const f of prFiles) {
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/projects/${pid}/prs/${saved.id}/attachments`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        } catch (err) { failures.push(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`); }
      }
      for (let i = 0; i < lines.length; i++) {
        const f = lines[i]._doc;
        if (!f) continue;
        const uid = lines[i].line_uid || saved.line_items?.[i]?.line_uid;
        if (!uid) { failures.push(`${f.name}: line reference missing`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        try {
          await api.post(`/projects/${pid}/prs/${saved.id}/lines/${uid}/attachment`, fd,
            { headers: { "Content-Type": "multipart/form-data" } });
        } catch (err) { failures.push(`${f.name}: ${err?.response?.data?.detail || "upload failed"}`); }
      }
      toast.success(existing ? "PR updated" : `PR ${saved.pr_number} created (Draft)`);
      if (failures.length) toast.error(`Some attachments failed — ${failures.join(" · ")}`, { duration: 8000 });
      onSaved(saved);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const inp = "border border-slate-300 rounded px-2 py-1 text-xs w-full";
  return (
    <div className="border border-orange-300 bg-orange-50/40 rounded-lg p-4 space-y-3" data-testid="pr-form">
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-slate-900">
          {existing ? `Edit ${existing.pr_number}` : "New Purchase Requisition"}
          <span className="ml-2 text-[10px] text-slate-500 font-normal">Project locked: {project.project_code}</span>
        </div>
        <button data-testid="pr-form-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
      </div>
      <div className="grid md:grid-cols-4 gap-2">
        <label className="text-xs">PR Date *
          <input data-testid="pr-date" type="date" className={inp} value={head.pr_date}
            onChange={e => setHead(h => ({ ...h, pr_date: e.target.value }))} /></label>
        <label className="text-xs">Proposed Vendor * <span className="text-[9px] text-slate-500">(searchable · bound by ID, never by name)</span>
          <SearchableSelect testid="pr-vendor" className="w-full"
            items={vendors} value={head.vendor_id}
            onChange={(id) => setHead(h => ({ ...h, vendor_id: id }))}
            getLabel={v => `${v.vendor_code ? `${v.vendor_code} · ` : ""}${v.name}`}
            getSubLabel={v => [v.skill_type ? `Tag: ${v.skill_type}` : "", v.gst_number ? `GST ${v.gst_number}` : ""].filter(Boolean).join(" · ")}
            getSearchText={v => `${v.vendor_code || ""} ${v.name || ""} ${v.skill_type || ""} ${v.gst_number || ""}`}
            placeholder="— select vendor —" searchPlaceholder="Search vendor by name / code / tag / GST…" /></label>
        <label className="text-xs">Required By
          <input data-testid="pr-required-by" type="date" className={inp} value={head.required_by_date}
            onChange={e => setHead(h => ({ ...h, required_by_date: e.target.value }))} /></label>
        <label className="text-xs">Priority
          <select data-testid="pr-priority" className={inp} value={head.priority}
            onChange={e => setHead(h => ({ ...h, priority: e.target.value }))}>
            {PRIORITIES.map(p => <option key={p} value={p}>{p || "—"}</option>)}
          </select></label>
        <label className="text-xs md:col-span-2">Delivery Location <span className="text-[9px] text-slate-500">(auto from Project Address — editable)</span>
          <input data-testid="pr-delivery" className={inp} value={head.delivery_location}
            onChange={e => setHead(h => ({ ...h, delivery_location: e.target.value }))} /></label>
        <label className="text-xs md:col-span-2">Purpose
          <input data-testid="pr-purpose" className={inp} value={head.purpose}
            onChange={e => setHead(h => ({ ...h, purpose: e.target.value }))} /></label>
        <label className="text-xs md:col-span-4">General Remarks
          <input data-testid="pr-remarks" className={inp} value={head.remarks}
            onChange={e => setHead(h => ({ ...h, remarks: e.target.value }))} /></label>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Filter items by Group:</label>
        <select data-testid="pr-group-filter" value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                className="border border-slate-300 rounded px-3 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none">
          <option value="">All Groups ({items.length} items)</option>
          {groups.map(g => {
            const count = items.filter(it => itemInGroup(it, g)).length;
            return <option key={g.id} value={g.id}>{g.name} ({count})</option>;
          })}
        </select>
        {groupFilter && (
          <button type="button" data-testid="pr-group-filter-clear" onClick={() => setGroupFilter("")}
            className="text-[10px] text-slate-500 hover:text-[#F97316] underline">clear</button>
        )}
      </div>

      <div className="overflow-x-auto border border-slate-200 rounded bg-white">
        <table className="w-full text-xs" data-testid="pr-form-lines">
          <thead className="bg-slate-100 text-[10px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-2 py-1.5 text-left">Item * <span className="normal-case text-slate-400">(from Item Master)</span></th>
              <th className="px-2 py-1.5 text-left">UOM *</th>
              <th className="px-2 py-1.5 text-right">Qty *</th>
              {commercialsVisible && <th className="px-2 py-1.5 text-right">Est Rate</th>}
              {commercialsVisible && <th className="px-2 py-1.5">Tax %</th>}
              {commercialsVisible && <th className="px-2 py-1.5 text-right">Unit ₹ <span className="normal-case text-slate-400">(inc GST)</span></th>}
              {commercialsVisible && <th className="px-2 py-1.5 text-right">Total ₹ <span className="normal-case text-slate-400">(inc GST)</span></th>}
              <th className="px-2 py-1.5">Req Date</th>
              <th className="px-2 py-1.5">BOQ Element (optional)</th>
              <th className="px-2 py-1.5">Work Activity</th>
              <th className="px-2 py-1.5">Doc <span className="normal-case text-slate-400">(img any size · pdf ≤500 KB)</span></th>
              <th className="px-2 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className="border-t border-slate-100 align-top">
                <td className="px-2 py-1">
                  <SearchableSelect testid={`pr-line-item-${i}`} className="w-60"
                    items={pickerItemsFor(l)} value={l.item_id}
                    onChange={(id, it) => {
                      if (!it) { setLine(i, { item_id: "", item_code: "", item_name: "", item_type_snapshot: "" }); return; }
                      const libUom = uoms.find(u => u.toLowerCase() === String(it.unit || "").toLowerCase()) || "";
                      setLine(i, { item_id: it.id, item_code: it.item_code || "", item_name: it.name || "",
                                   item_type_snapshot: it.item_type || "", uom: libUom || l.uom });
                    }}
                    getLabel={it => `${it.item_code ? `${it.item_code} · ` : ""}${it.name || ""}`}
                    getSubLabel={it => [it.item_type, it.group_name].filter(Boolean).join(" · ")}
                    getSearchText={it => `${it.item_code || ""} ${it.name || ""} ${it.item_type || ""} ${it.group_name || ""} ${it.hsn_code || ""}`}
                    placeholder="Search item…" searchPlaceholder="Search Item Master by code / name / group…" />
                  {l.item_type_snapshot && <div className="text-[9px] uppercase text-slate-500 mt-0.5">{l.item_type_snapshot}</div>}
                </td>
                <td className="px-2 py-1">
                  <SearchableSelect testid={`pr-line-uom-${i}`} className="w-28"
                    items={uoms.map(u => ({ id: u, name: u }))} value={l.uom}
                    onChange={(id) => setLine(i, { uom: id })}
                    placeholder="— UOM —" searchPlaceholder="Search UOM…" />
                </td>
                <td className="px-2 py-1"><input data-testid={`pr-line-qty-${i}`} type="number" min="0" step="any" className="border border-slate-300 rounded px-1 py-1 text-xs w-20 text-right" value={l.quantity} onChange={e => setLine(i, { quantity: e.target.value })} /></td>
                {commercialsVisible && <td className="px-2 py-1"><input data-testid={`pr-line-rate-${i}`} type="number" min="0" step="any" placeholder="blank ok" className="border border-slate-300 rounded px-1 py-1 text-xs w-24 text-right" value={l.estimated_rate} onChange={e => setLine(i, { estimated_rate: e.target.value })} /></td>}
                {commercialsVisible && <td className="px-2 py-1">
                  <select data-testid={`pr-line-tax-${i}`} className="border border-slate-300 rounded px-1 py-1 text-xs w-16" value={l.tax_percent} onChange={e => setLine(i, { tax_percent: e.target.value })}>
                    {TAX_OPTS.map(t => <option key={t} value={t}>{t === "" ? "—" : t}</option>)}
                  </select></td>}
                {commercialsVisible && (() => {
                  const r = l.estimated_rate === "" ? null : parseFloat(l.estimated_rate);
                  const q = parseFloat(l.quantity);
                  const factor = 1 + (parseFloat(l.tax_percent) || 0) / 100;
                  const unitInc = r === null || Number.isNaN(r) ? null : r * factor;
                  const totInc = unitInc === null || Number.isNaN(q) ? null : unitInc * q;
                  const canBackfill = !Number.isNaN(q) && q > 0;
                  return (<>
                    <td className="px-2 py-1 text-right tabular-nums text-slate-700 whitespace-nowrap" data-testid={`pr-line-unit-incgst-${i}`}>{unitInc === null ? "—" : formatINR(unitInc)}</td>
                    <td className="px-2 py-1">
                      <input data-testid={`pr-line-total-incgst-${i}`} type="number" min="0" step="any"
                        className="border border-slate-300 rounded px-1 py-1 text-xs w-28 text-right font-bold disabled:bg-slate-50"
                        placeholder={canBackfill ? "type to back-fill" : "qty first"}
                        title={canBackfill ? "Type a total (inc GST) — Est Rate and Unit ₹ (inc GST) back-fill automatically" : "Enter Qty first to back-fill from a total"}
                        disabled={!canBackfill}
                        value={totEdit[i] !== undefined ? totEdit[i] : (totInc === null ? "" : String(Math.round(totInc * 100) / 100))}
                        onChange={e => {
                          const v = e.target.value;
                          setTotEdit(x => ({ ...x, [i]: v }));
                          const t = parseFloat(v);
                          if (Number.isNaN(t)) { setLine(i, { estimated_rate: "" }); return; }
                          setLine(i, { estimated_rate: String(Math.round((t / (q * factor)) * 1e6) / 1e6) });
                        }}
                        onBlur={() => setTotEdit(x => { const n = { ...x }; delete n[i]; return n; })} />
                    </td>
                  </>);
                })()}
                <td className="px-2 py-1"><input type="date" className="border border-slate-300 rounded px-1 py-1 text-xs w-32" value={l.required_date} onChange={e => setLine(i, { required_date: e.target.value })} /></td>
                <td className="px-2 py-1">
                  <SearchableSelect testid={`pr-line-boq-${i}`} className="w-48"
                    items={boqEls} value={l.boq_element_id || ""}
                    onChange={(id) => setLine(i, { boq_element_id: id })}
                    getLabel={el => `${el.element_code} · ${(el.name || "").slice(0, 40)}`}
                    getSubLabel={el => el.category_display}
                    getSearchText={el => `${el.element_code} ${el.name} ${el.category_display}`}
                    placeholder="— none —" searchPlaceholder="Search BOQ element…" />
                </td>
                <td className="px-2 py-1"><input className="border border-slate-300 rounded px-1 py-1 text-xs w-32" value={l.work_activity} onChange={e => setLine(i, { work_activity: e.target.value })} /></td>
                <td className="px-2 py-1 whitespace-nowrap">
                  {l.attachment ? (
                    <span className="text-[10px] text-emerald-700 font-bold inline-flex items-center gap-1" title={l.attachment.filename}>
                      <Paperclip size={11} /> attached
                    </span>
                  ) : l._doc ? (
                    <span className="text-[10px] text-sky-700 inline-flex items-center gap-1 max-w-[110px]" title={l._doc.name}>
                      <Paperclip size={11} /><span className="truncate">{l._doc.name}</span>
                      <button data-testid={`pr-line-doc-clear-${i}`} onClick={() => setLine(i, { _doc: null })} className="text-rose-600"><X size={10} /></button>
                    </span>
                  ) : (
                    <label className="text-[10px] text-slate-500 hover:text-slate-800 cursor-pointer inline-flex items-center gap-1" title="Attach PDF/JPG/PNG — images any size (auto-compressed below 500 KB) · PDF ≤500 KB">
                      <Paperclip size={11} /> Add
                      <input data-testid={`pr-line-doc-${i}`} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                        onChange={(e) => pickLineDoc(i, e)} />
                    </label>
                  )}
                </td>
                <td className="px-2 py-1">
                  <button data-testid={`pr-line-del-${i}`} onClick={() => setLines(ls => ls.filter((_, j) => j !== i))} className="text-rose-600 hover:text-rose-800"><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="border border-slate-200 rounded bg-white px-3 py-2 space-y-1" data-testid="pr-form-attachments">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase font-bold text-slate-500">PR Attachments ({existingAtt + prFiles.length}/3)</span>
          <span className="text-[9px] text-slate-400">PDF ≤5 MB · images any size (auto-compressed below 500 KB) · uploaded on save</span>
          {existingAtt + prFiles.length < 3 && (
            <label className="text-[11px] font-bold text-sky-700 hover:underline cursor-pointer inline-flex items-center gap-1">
              <Paperclip size={12} /> Attach file
              <input data-testid="pr-form-attach-input" type="file" multiple className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={pickPrFiles} />
            </label>
          )}
        </div>
        {(existingAtt > 0 || prFiles.length > 0) && (
          <div className="flex flex-wrap gap-1.5">
            {(existing?.attachments || []).map(a => (
              <span key={a.id} className="text-[10px] bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-600" title="already uploaded">{a.filename}</span>
            ))}
            {prFiles.map((f, i) => (
              <span key={i} className="text-[10px] bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5 text-sky-800 inline-flex items-center gap-1" data-testid={`pr-form-file-${i}`}>
                {f.name} ({Math.ceil(f.size / 1024)} KB)
                <button data-testid={`pr-form-file-remove-${i}`} onClick={() => setPrFiles(fs => fs.filter((_, j) => j !== i))} className="text-rose-600"><X size={10} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button data-testid="pr-add-line" onClick={() => setLines(ls => [...ls, emptyLine()])}
          className="px-2 py-1 text-xs font-bold border border-slate-300 rounded bg-white hover:bg-slate-50 inline-flex items-center gap-1"><Plus size={12} /> Add Line</button>
        <div className="flex items-center gap-3">
          {commercialsVisible && (
            <div className="text-xs text-slate-600" data-testid="pr-form-totals">
              Est: <b className="tabular-nums">{formatINR(totals.sub)}</b> + tax <b className="tabular-nums">{formatINR(totals.tax)}</b> = <b className="tabular-nums">{formatINR(totals.total)}</b>
              <span className="text-[9px] text-slate-400 ml-1">(server recalculates)</span>
            </div>
          )}
          <button data-testid="pr-save-btn" disabled={busy} onClick={save}
            className="px-3 py-1.5 text-xs font-bold bg-[#0B1F3A] text-white rounded hover:bg-[#13294f] inline-flex items-center gap-1.5">
            <Save size={13} /> {existing ? "Save Changes" : "Create Draft PR"}
          </button>
        </div>
      </div>
    </div>
  );
}
